import React, { useCallback, useEffect, useState } from 'react';

import { canEditRestaurantStoreProfile } from './deliveryAgentSession';
import {
  DATE_FORMAT_OPTIONS,
  emptyOrderNumberForm,
  fetchOrderNumberConfig,
  saveOrderNumberConfig,
  validateOrderNumberForm,
} from './orderNumberingApi';
import './css/restaurantServices.css';

const formatDisplayValue = (value) => {
  if (value === '' || value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const OrderNumberingDisplay = ({ restaurantId, storeId, hideTitle = false }) => {
  const [form, setForm] = useState(emptyOrderNumberForm);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEdit = canEditRestaurantStoreProfile();

  const entityId = restaurantId ?? storeId;
  const isStore = storeId != null && restaurantId == null;

  const loadConfig = useCallback(async () => {
    const id = entityId != null ? String(entityId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setSaved(null);
      setForm(emptyOrderNumberForm());
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const cfg = await fetchOrderNumberConfig(
        isStore ? { storeId: id } : { restaurantId: id }
      );
      setSaved(cfg);
      setForm(cfg);
    } catch (e) {
      console.error('Failed to load order numbering config:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load order numbering settings.'
      );
      setSaved(null);
      setForm(emptyOrderNumberForm());
    } finally {
      setLoading(false);
    }
  }, [entityId, isStore]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const startEdit = () => {
    setForm(saved || emptyOrderNumberForm());
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setForm(saved || emptyOrderNumberForm());
    setIsEditing(false);
  };

  const handleFieldChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    const validationError = validateOrderNumberForm(form);
    if (validationError) {
      window.alert(validationError);
      return;
    }

    const id = entityId != null ? String(entityId).trim() : '';
    if (!id) return;

    setSaving(true);
    try {
      const userRaw = localStorage.getItem('user');
      let updatedById = null;
      try {
        const user = userRaw ? JSON.parse(userRaw) : null;
        updatedById = user?.userId ?? user?.user_id ?? null;
      } catch {
        updatedById = null;
      }
      const entityPayload = isStore ? { storeId: id } : { restaurantId: id };
      const next = await saveOrderNumberConfig({
        ...entityPayload,
        form,
        updatedById,
      });
      setSaved(next);
      setForm(next);
      setIsEditing(false);
      window.alert('Order numbering settings saved successfully.');
    } catch (e) {
      console.error('Failed to save order numbering config:', e);
      window.alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to save order numbering settings.'
      );
    } finally {
      setSaving(false);
    }
  };

  const view = saved || emptyOrderNumberForm();

  return (
    <div className="restaurant-detail-card restaurant-detail-taxes-card">
      <div
        className={`restaurant-detail-card-header restaurant-services-card-header${
          hideTitle ? ' restaurant-detail-card-header--badge-only' : ''
        }`}
      >
        {hideTitle ? null : (
          <h3 className="restaurant-detail-section-title">Order Numbering</h3>
        )}
        {canEdit && !isEditing ? (
          <button
            type="button"
            className="eats-admin-header-btn"
            onClick={startEdit}
            disabled={loading || saving}
          >
            Edit
          </button>
        ) : null}
        {canEdit && isEditing ? (
          <div className="restaurant-detail-edit-actions restaurant-services-actions">
            <button
              type="button"
              className="eats-admin-header-btn"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="eats-admin-header-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </div>

      {loading ? <p className="restaurant-detail-message">Loading order numbering…</p> : null}
      {!loading && error ? <p className="restaurant-detail-message error">{error}</p> : null}

      {!loading && !error ? (
        isEditing ? (
          <div className="restaurant-detail-edit-form restaurant-taxes-form">
            <div className="get-restaurant-form-row flex restaurant-taxes-form-grid">
              <div className="get-restaurant-form-group">
                <label>Enabled</label>
                <select
                  value={form.orderNoEnabled ? 'true' : 'false'}
                  onChange={(e) => handleFieldChange('orderNoEnabled', e.target.value === 'true')}
                  disabled={saving}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="get-restaurant-form-group">
                <label>Prefix</label>
                <input
                  type="text"
                  maxLength={20}
                  value={form.orderNoPrefix}
                  onChange={(e) => handleFieldChange('orderNoPrefix', e.target.value)}
                  disabled={saving}
                  placeholder="e.g. MSR"
                />
              </div>
              <div className="get-restaurant-form-group">
                <label>Include date</label>
                <select
                  value={form.orderNoIncludeDate ? 'true' : 'false'}
                  onChange={(e) =>
                    handleFieldChange('orderNoIncludeDate', e.target.value === 'true')
                  }
                  disabled={saving}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="get-restaurant-form-group">
                <label>Date format</label>
                <select
                  value={form.orderNoDateFormat}
                  onChange={(e) => handleFieldChange('orderNoDateFormat', e.target.value)}
                  disabled={saving || !form.orderNoIncludeDate}
                >
                  {DATE_FORMAT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="get-restaurant-form-group">
                <label>Pad width</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="1"
                  value={form.orderNoPadWidth}
                  onChange={(e) => handleFieldChange('orderNoPadWidth', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="get-restaurant-form-group">
                <label>Separator</label>
                <input
                  type="text"
                  maxLength={5}
                  value={form.orderNoSeparator}
                  onChange={(e) => handleFieldChange('orderNoSeparator', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="get-restaurant-form-group">
                <label>Timezone</label>
                <input
                  type="text"
                  value={form.orderNoTimezone}
                  onChange={(e) => handleFieldChange('orderNoTimezone', e.target.value)}
                  disabled={saving}
                  placeholder="Asia/Kolkata"
                />
              </div>
            </div>
            <p className="restaurant-detail-message" style={{ marginTop: '0.75rem' }}>
              Example:{' '}
              {[
                form.orderNoPrefix?.trim(),
                form.orderNoIncludeDate
                  ? form.orderNoDateFormat === 'DDMon'
                    ? '10Sep'
                    : form.orderNoDateFormat === 'DDMMYYYY'
                      ? '10092026'
                      : '20260910'
                  : null,
                String(1).padStart(Number(form.orderNoPadWidth) || 0, '0'),
              ]
                .filter(Boolean)
                .join(form.orderNoSeparator ?? '-')}
            </p>
          </div>
        ) : (
          <div className="restaurant-detail-grid">
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Enabled</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoEnabled)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Prefix</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoPrefix)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Include date</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoIncludeDate)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Date format</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoDateFormat)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Pad width</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoPadWidth)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Separator</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoSeparator)}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Timezone</span>
              <span className="restaurant-detail-value">
                {formatDisplayValue(view.orderNoTimezone)}
              </span>
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

export default OrderNumberingDisplay;
